import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { BugStatusBoard } from '@/components/bugs/BugStatusBoard';
import { BugReportDialog } from '@/components/layout/BugReportDialog';

type Project = { id: string; name: string; columns: { id: string; name: string }[] };
type Category = { key: string; label: string; project_id: string | null; column_id: string };
type BoardColumn = { column_id: string; label?: string; project_id?: string | null };
type Config = {
  project_id: string | null;
  target_column_id: string | null;
  projects: Project[];
  categories: Category[];
  board_columns: BoardColumn[];
};
type Connection = {
  base_url: string | null;
  integration_id: string | null;
  has_key: boolean;
  enabled: boolean;
  active: boolean;
  source: string;
};
type Result<T> = { success: boolean; data?: T; error?: string };
function unwrap<T>(result: Result<T>): T {
  if (!result.success || !result.data) throw new Error(result.error || 'Daten konnten nicht geladen werden.');
  return result.data;
}
const endpoint = '/admin/bug-reports';

function Choice({
  id,
  label,
  value,
  items,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  items: { id: string; name: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={items.some((i) => i.id === value) ? value : ''} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Bitte wählen" />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function BugReportTargetPanel() {
  const [connection, setConnection] = useState<Connection | null>(null);
  const [baseUrl, setBaseUrl] = useState('https://projects.leftclick.ch');
  const [integrationId, setIntegrationId] = useState('');
  const [key, setKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState<Config | null>(null);
  const [project, setProject] = useState('');
  const [column, setColumn] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [boardColumns, setBoardColumns] = useState<BoardColumn[]>([]);
  const [busy, setBusy] = useState('load');
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [revision, setRevision] = useState(0);

  const loadConfig = useCallback(async () => {
    const data = unwrap(await api.get<Result<Config>>(`${endpoint}/configuration`));
    setConfig(data);
    setProject(data.project_id || '');
    setColumn(data.target_column_id || '');
    setCategories(data.categories);
    setBoardColumns(data.board_columns);
  }, []);
  const loadConnection = useCallback(async () => {
    const data = unwrap(await api.get<Result<Connection>>(`${endpoint}/connection`));
    setConnection(data);
    setBaseUrl(data.base_url || 'https://projects.leftclick.ch');
    setIntegrationId(data.integration_id || '');
    setEnabled(data.enabled);
    setKey('');
    return data;
  }, []);
  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusy('');
    }
  };
  useEffect(() => {
    void run('load', async () => {
      const data = await loadConnection();
      if (data.active) await loadConfig();
    });
  }, [loadConnection, loadConfig]);

  const draft = { base_url: baseUrl, integration_id: integrationId, admin_key: key, enabled };
  const dirty =
    !!key ||
    baseUrl !== (connection?.base_url || 'https://projects.leftclick.ch') ||
    integrationId !== (connection?.integration_id || '') ||
    enabled !== connection?.enabled;
  const projects = config?.projects || [];
  const columns = projects.find((p) => p.id === project)?.columns || [];
  const allColumns = projects.flatMap((p) => p.columns.map((c) => ({ ...c, project: p.name })));
  const validColumn = (id: string) => allColumns.some((c) => c.id === id);
  const categoriesValid =
    categories.every(
      (c) =>
        /^[a-z0-9][a-z0-9_-]{0,39}$/.test(c.key) &&
        c.label.trim() &&
        c.label.trim().length <= 60 &&
        validColumn(c.column_id),
    ) && new Set(categories.map((c) => c.key)).size === categories.length;
  const saveConfig = async (body: unknown, message: string) => {
    const result = await api.patch<Result<unknown>>(`${endpoint}/configuration`, body);
    if (!result.success) throw new Error(result.error || 'Speichern fehlgeschlagen.');
    toast.success(message);
    setRevision((v) => v + 1);
    window.dispatchEvent(new Event('bug-report-settings-changed'));
  };
  const updateCategory = (index: number, changes: Partial<Category>) =>
    setCategories((list) => list.map((c, i) => (i === index ? { ...c, ...changes } : c)));
  const moveColumn = (index: number, offset: number) =>
    setBoardColumns((list) => {
      const next = [...list];
      [next[index], next[index + offset]] = [next[index + offset]!, next[index]!];
      return next;
    });

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Verbindung zu Leftclick Projects</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <fieldset disabled={!!busy} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bug-address">Leftclick Projects-Adresse</Label>
              <Input
                voiceInput={false}
                id="bug-address"
                value={baseUrl}
                onChange={(e) => {
                  setBaseUrl(e.target.value);
                  setTestResult('');
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bug-integration">Integrations-ID</Label>
              <Input
                voiceInput={false}
                id="bug-integration"
                value={integrationId}
                onChange={(e) => {
                  setIntegrationId(e.target.value);
                  setTestResult('');
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bug-key">Admin-Schlüssel</Label>
              <Input
                id="bug-key"
                type="password"
                autoComplete="new-password"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value);
                  setTestResult('');
                }}
                placeholder={
                  connection?.has_key ? 'Gespeichert. Leer lassen zum Beibehalten.' : 'Schlüssel eingeben'
                }
              />
              <p className="text-xs text-muted">Wird verschlüsselt gespeichert und danach nicht angezeigt.</p>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="bug-enabled">Bugmeldungen aktivieren</Label>
              <Switch id="bug-enabled" checked={enabled} onCheckedChange={setEnabled} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={!baseUrl.trim() || !integrationId.trim()}
                onClick={() =>
                  void run('test', async () => {
                    setTestResult('');
                    const data = unwrap(
                      await api.post<Result<{ name: string | null; project_count: number }>>(
                        `${endpoint}/connection/test`,
                        draft,
                      ),
                    );
                    setTestResult(
                      `Verbindung erfolgreich${data.name ? `: ${data.name}` : ''}. ${data.project_count} Projekte freigegeben.`,
                    );
                  })
                }
              >
                {busy === 'test' ? 'Teste…' : 'Verbindung testen'}
              </Button>
              <Button
                disabled={!dirty || !baseUrl.trim() || !integrationId.trim()}
                onClick={() =>
                  void run('save', async () => {
                    const result = await api.put<Result<unknown>>(`${endpoint}/connection`, draft);
                    if (!result.success) throw new Error(result.error || 'Speichern fehlgeschlagen.');
                    setConfig(null);
                    const data = await loadConnection();
                    setRevision((v) => v + 1);
                    window.dispatchEvent(new Event('bug-report-settings-changed'));
                    toast.success('Verbindung gespeichert.');
                    if (data.active) await loadConfig();
                  })
                }
              >
                Verbindung speichern
              </Button>
            </div>
          </fieldset>
          {testResult && (
            <p role="status" className="text-sm">
              {testResult}
            </p>
          )}
          <p className="text-xs text-muted">
            Der Verbindungstest speichert nichts und erstellt keine Meldung.
          </p>
        </CardContent>
      </Card>
      {error && (
        <div
          role="alert"
          className="rounded-md border border-danger/30 bg-danger-soft p-3 text-sm text-danger"
        >
          {error}
        </div>
      )}
      {connection?.active && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              disabled={!!busy || dirty}
              onClick={() => void run('reload', loadConfig)}
            >
              Konfiguration neu laden
            </Button>
            {dirty && <span className="text-sm text-muted">Verbindungsänderungen zuerst speichern.</span>}
          </div>
          {config && (
            <fieldset disabled={!!busy || dirty} className="space-y-5 disabled:opacity-60">
              <Card>
                <CardHeader>
                  <CardTitle>Ziel der Bugmeldungen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!projects.length && (
                    <p className="text-sm text-muted">
                      In Leftclick Projects zuerst Team-Projekte für diese Verbindung freigeben.
                    </p>
                  )}
                  {!!projects.length && !columns.some((c) => c.id === column) && (
                    <p className="text-sm text-muted">Bitte Ziel neu auswählen.</p>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Choice
                      id="bug-project"
                      label="Projekt"
                      value={project}
                      items={projects}
                      onChange={(id) => {
                        setProject(id);
                        setColumn('');
                      }}
                    />
                    <Choice
                      id="bug-column"
                      label="Spalte"
                      value={column}
                      items={columns}
                      onChange={setColumn}
                    />
                  </div>
                  <Button
                    disabled={!columns.some((c) => c.id === column)}
                    onClick={() =>
                      void run('target', () =>
                        saveConfig({ project_id: project, target_column_id: column }, 'Ziel gespeichert.'),
                      )
                    }
                  >
                    Ziel speichern
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Kategorien</CardTitle>
                  <p className="text-sm text-muted">Jede Kategorie leitet Meldungen in ihre Zielspalte.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {categories.map((category, index) => (
                    <div key={index} className="space-y-2 rounded-md border border-border p-3">
                      <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <div className="space-y-1.5">
                          <Label htmlFor={`cat-name-${index}`}>Name</Label>
                          <Input
                            id={`cat-name-${index}`}
                            value={category.label}
                            maxLength={60}
                            onChange={(e) => updateCategory(index, { label: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`cat-key-${index}`}>Schlüssel</Label>
                          <Input
                            voiceInput={false}
                            id={`cat-key-${index}`}
                            value={category.key}
                            maxLength={40}
                            onChange={(e) => updateCategory(index, { key: e.target.value })}
                          />
                        </div>
                        <Choice
                          id={`cat-project-${index}`}
                          label="Projekt"
                          value={category.project_id || ''}
                          items={projects}
                          onChange={(id) => updateCategory(index, { project_id: id, column_id: '' })}
                        />
                        <Choice
                          id={`cat-column-${index}`}
                          label="Spalte"
                          value={category.column_id}
                          items={projects.find((p) => p.id === category.project_id)?.columns || []}
                          onChange={(id) => updateCategory(index, { column_id: id })}
                        />
                      </div>
                      {!validColumn(category.column_id) && (
                        <p className="text-xs text-muted">Spalte nicht verfügbar – bitte auswählen.</p>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Kategorie ${index + 1} entfernen`}
                        onClick={() => setCategories((list) => list.filter((_, i) => i !== index))}
                      >
                        <Trash2 />
                        Entfernen
                      </Button>
                    </div>
                  ))}
                  {!!categories.length && !categoriesValid && (
                    <p className="text-xs text-muted">
                      Name, eindeutiger Schlüssel (a–z, 0–9, - oder _) und gültige Spalte erforderlich.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      disabled={categories.length >= 20}
                      onClick={() =>
                        setCategories((list) => [
                          ...list,
                          { key: '', label: '', project_id: project || null, column_id: '' },
                        ])
                      }
                    >
                      <Plus />
                      Kategorie hinzufügen
                    </Button>
                    <Button
                      disabled={!categoriesValid}
                      onClick={() =>
                        void run('categories', () =>
                          saveConfig(
                            {
                              categories: categories.map(({ key, label, column_id }) => ({
                                key,
                                label,
                                column_id,
                              })),
                            },
                            'Kategorien gespeichert.',
                          ),
                        )
                      }
                    >
                      Kategorien speichern
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Status-Board</CardTitle>
                  <p className="text-sm text-muted">
                    Spalten auswählen, benennen und sortieren. Ohne Spalten ist das Board ausgeschaltet.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {boardColumns.map((item, index) => {
                    const source = allColumns.find((c) => c.id === item.column_id);
                    return (
                      <div
                        key={item.column_id}
                        className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3"
                      >
                        <span className="min-w-40 flex-1 text-sm">
                          {source ? `${source.project} / ${source.name}` : 'Spalte nicht mehr verfügbar'}
                        </span>
                        <div className="w-full sm:w-48">
                          <Input
                            aria-label={`Anzeigename Spalte ${index + 1}`}
                            placeholder="Spaltenname verwenden"
                            value={item.label || ''}
                            maxLength={60}
                            onChange={(e) =>
                              setBoardColumns((list) =>
                                list.map((c, i) => (i === index ? { ...c, label: e.target.value } : c)),
                              )
                            }
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label={`Spalte ${index + 1} nach oben`}
                          disabled={index === 0}
                          onClick={() => moveColumn(index, -1)}
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label={`Spalte ${index + 1} nach unten`}
                          disabled={index === boardColumns.length - 1}
                          onClick={() => moveColumn(index, 1)}
                        >
                          <ArrowDown />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Spalte ${index + 1} entfernen`}
                          onClick={() => setBoardColumns((list) => list.filter((_, i) => i !== index))}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    );
                  })}
                  {boardColumns.length < 30 && (
                    <Choice
                      id="bug-board-add"
                      label="Spalte hinzufügen"
                      value=""
                      items={allColumns
                        .filter((c) => !boardColumns.some((b) => b.column_id === c.id))
                        .map((c) => ({ id: c.id, name: `${c.project} / ${c.name}` }))}
                      onChange={(id) => setBoardColumns((list) => [...list, { column_id: id, label: '' }])}
                    />
                  )}
                  <Button
                    disabled={boardColumns.some((c) => !validColumn(c.column_id))}
                    onClick={() =>
                      void run('board', () =>
                        saveConfig(
                          {
                            board_columns: boardColumns.map(({ column_id, label }) => ({ column_id, label })),
                          },
                          'Board gespeichert.',
                        ),
                      )
                    }
                  >
                    Board speichern
                  </Button>
                </CardContent>
              </Card>
            </fieldset>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Meldungen prüfen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted">
                Eine Testmeldung wird als echte Aufgabe im gespeicherten Ziel angelegt.
              </p>
              <Button variant="outline" disabled={!!busy || dirty} onClick={() => setReportOpen(true)}>
                Testmeldung verfassen
              </Button>
              <BugStatusBoard scope="all" revision={revision} />
            </CardContent>
          </Card>
          <BugReportDialog
            open={reportOpen}
            onOpenChange={setReportOpen}
            onSent={() => setRevision((v) => v + 1)}
          />
        </>
      )}
      {busy === 'load' && (
        <p role="status" className="text-sm text-muted">
          Einstellungen werden geladen…
        </p>
      )}
    </div>
  );
}
